import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC } from 'react';
import { useMemo } from 'react';
import { DropdownTrigger } from 'src/components/buttons-and-actions/dropdown-trigger';
import type { LinkProps } from 'src/components/navigation/link';
import { Link } from 'src/components/navigation/link';
import { useSearchParams } from 'src/hooks/use-search-params';
import { cn } from 'src/utils/cn';

export type PaginationProps = (
  | {
      start: number;
      totalSize: number;
      perPage: number;
      offset?: undefined;
      total?: undefined;
      limit?: undefined;
    }
  | {
      start?: undefined;
      totalSize?: undefined;
      perPage?: undefined;
      offset: number;
      total: number;
      limit: number;
    }
) & {
  paramPrefix?: string;
  configurable?: boolean;
};

/**
 * Most of the time the `<Pagination />` component is used inside our
 * `<Table />` component, but in case you need to use it _outside_ of a table,
 * you can use this dedicated component.
 */
export const Pagination: FC<PaginationProps> = (props) => {
  const { offset, total, limit, configurable, paramPrefix } =
    normalizePaginationProps(props);

  const params = useSearchParams();

  const startKey = paramPrefix ? `${paramPrefix}start` : 'start';
  const pageSizeKey = paramPrefix ? `${paramPrefix}pageSize` : 'pageSize';

  const { end, lastPage, currentPage, pages } = useMemo(() => {
    const currentPage = Math.floor(offset / limit) + 1;
    const lastPage = Math.floor((total - 1) / limit) + 1;

    const slice = offsets
      .map((offset) => currentPage + offset)
      .filter((page) => {
        const items = page * limit;
        return items > 0 && items < total + limit;
      });

    const firstInSlice = slice[0];
    const lastInSlice = slice[slice.length - 1];

    const pages: Array<number | null> = [...slice];

    if (lastInSlice !== lastPage) {
      // removes up to two items on end and appends [null, lastPage]
      const deleteCount = clamp(currentPage - firstInSlice, 0, 2);
      pages.splice(pages.length - deleteCount, deleteCount, null, lastPage);
    }
    if (firstInSlice !== 1) {
      // removes up to two items on start and prepends [1, null]
      const deleteCount = clamp(lastInSlice - currentPage, 0, 2);
      pages.splice(0, deleteCount, 1, null);
    }

    const end = offset + limit > total ? total : offset + limit;

    return { end, lastPage, currentPage, pages };
  }, [limit, offset, total]);

  const createPageLink = (start: number) => {
    const newParams = new URLSearchParams(params);
    if (start) {
      newParams.set(startKey, start.toString());
    } else {
      newParams.delete(startKey);
    }
    return `?${newParams}`;
  };

  const testidPrefix = paramPrefix ? `${paramPrefix}-` : '';

  if (!total) return null;

  return (
    <div
      className={`
        flex items-center justify-between border-r border-b border-l
        border-legacy-brand-border bg-white px-md py-lg
        not-legacy:rounded-b-xs not-legacy:text-body-base
        not-legacy:text-foreground-default
        legacy:p-[1.3rem_0.8rem]
      `}
    >
      {configurable ? (
        <PaginationConfig
          start={offset + 1}
          end={end}
          total={total}
          pageSizeKey={pageSizeKey}
        />
      ) : (
        <div>
          {offset + 1}-{end} of {total}
        </div>
      )}

      {lastPage > 1 && (
        <div
          className={`
            grid grid-flow-col gap-md
            legacy:gap-[1rem]
          `}
          data-testid="pagination-control"
        >
          {currentPage !== 1 && (
            <PageLink
              to={createPageLink((currentPage - 2) * limit)}
              data-testid={`${testidPrefix}previous-page`}
            >
              <FontAwesomeIcon icon={faAngleLeft} aria-label="Previous page" />
            </PageLink>
          )}

          {pages.map((page, index) =>
            page !== null ? (
              <PageLink
                key={page}
                active={page === currentPage}
                to={createPageLink((page - 1) * limit)}
              >
                {page}
              </PageLink>
            ) : (
              <div key={`empty-${index}`}>...</div>
            )
          )}

          {currentPage !== lastPage && (
            <PageLink
              to={createPageLink(currentPage * limit)}
              data-testid={`${testidPrefix}next-page`}
            >
              <FontAwesomeIcon icon={faAngleRight} aria-label="Next page" />
            </PageLink>
          )}
        </div>
      )}
    </div>
  );
};

type InternalPaginationProps = {
  offset: number;
  total: number;
  limit: number;
  paramPrefix: string;
  configurable: boolean;
};

const offsets = [-4, -3, -2, -1, 0, 1, 2, 3, 4];

function clamp(num: number, min: number, max: number) {
  return num <= min ? min : num >= max ? max : num;
}

const PaginationConfig: FC<{
  start: number;
  end: number;
  total: number;
  pageSizeKey: string;
}> = (props) => {
  const { start, end, total, pageSizeKey } = props;

  const params = useSearchParams();
  const currentPageSize = Number(params.get(pageSizeKey)) || 10;

  const createPageLink = (items: number) => {
    const newParams = new URLSearchParams(params);

    newParams.set(pageSizeKey, items.toString());

    return `?${newParams}`;
  };

  return (
    <div className="flex" data-testid="pagination-config">
      <DropdownTrigger
        mode="link"
        label={`${start}-${end}`}
        data-testid="pagination-config-dropdown-trigger"
        className="legacy:border-b-0"
      >
        <Link isActive={currentPageSize === 10} to={createPageLink(10)}>
          10 Items
        </Link>
        <Link isActive={currentPageSize === 25} to={createPageLink(25)}>
          25 Items
        </Link>
        <Link isActive={currentPageSize === 50} to={createPageLink(50)}>
          50 Items
        </Link>
      </DropdownTrigger>
      <span className="whitespace-pre"> of {total}</span>
    </div>
  );
};

const PageLink: FC<LinkProps<any> & { active?: boolean }> = ({
  active,
  className,
  ...props
}) => (
  <Link
    {...props}
    className={cn(
      `legacy:border-b-0`,
      active &&
        `
          not-legacy:font-body-semibold not-legacy:text-foreground-default
          not-legacy:not-active:hover:text-foreground-default
          legacy:font-legacy-bold
        `,
      className
    )}
  />
);

export function normalizePaginationProps(
  pagination: PaginationProps
): InternalPaginationProps {
  const offset = pagination.offset ?? pagination.start;
  const total = pagination.totalSize ?? pagination.total;
  const limit = pagination.limit ?? pagination.perPage;
  return {
    offset,
    total,
    limit,
    configurable: pagination.configurable ?? true,
    paramPrefix: pagination.paramPrefix ?? '',
  };
}
